import type { Scenario } from './types'

// Fictional illustrative case. Authored content — not derived from any real user.
// Source: docs/scenario_pages_plan.md EP02 — "The Mood That Wouldn't Lift".

export const serotoninPriya: Scenario = {
  slug: 'serotonin-priya',
  series: 'Serotonin & Mood',
  title: "Her life looked fine. Her body disagreed.",
  socialHook:
    "Priya, 34: her life looked good on paper. She was anxious, flat, and exhausted anyway. Her genetics showed why — and what to do about it.",
  meta: {
    description:
      "A fictional case study: Priya's life looked stable, but she felt anxious, flat, and tired most days. Her results showed why stress hit harder, recovery took longer, and low folate mattered more for her mood chemistry.",
  },

  persona: {
    name: 'Priya',
    age: 34,
    sex: 'Female',
    ancestry: 'South Asian',
    occupation: 'UX designer',
    chiefConcern:
      "She calls herself 'a worrier by nature,' but the label no longer feels good enough. Since her mid-twenties she has been anxious, flat, and tired, even when life is going well. She wants to know whether her body is keeping her stuck — and what she can do about it.",
    familyHistory: "Mother: anxiety and depression, managed with medication. Maternal aunt: similar history.",
  },

  clinicalPicture: {
    symptoms: [
      'Persistent low-grade anxiety — racing thoughts, especially at night',
      'Flat mood most days: not deeply depressed, but rarely genuinely happy',
      'Fatigue that sleep doesn\'t fix (wakes feeling tired)',
      'Difficulty shaking off stressful events — "things affect me more than they affect other people"',
    ],
    labs: [
      { name: 'Ferritin (iron stores)', value: '18 µg/L', refRange: '20–200 µg/L', flag: 'low' },
      { name: 'Folate (blood)', value: '2.7 ng/mL', refRange: '>3.1 ng/mL', flag: 'low' },
    ],
    medications: ['No prescription medications', 'Previously tried two SSRIs — partial response, stopped due to side effects'],
    supplements: ['Occasional multivitamin (inconsistent)'],
    lifestyle: [
      'Sleep: 7 hours but unrefreshing; falls asleep easily, wakes at 4–5am with thoughts',
      'Exercise: yoga 1–2x/week, no aerobic activity',
      'Diet: vegetarian, tends to skip breakfast, high in refined carbs',
      'Low caffeine (one cup of tea per day)',
      'Non-smoker, alcohol 2–3 drinks/week',
    ],
  },

  genetics: [
    {
      gene: 'SLC6A4',
      variant: 'Short/short (5-HTTLPR)',
      rsid: 'rs25531 / insertion-deletion polymorphism',
      genotype: 'Two short alleles',
      plainMeaning:
        "Her nervous system is wired to feel stress more sharply — and to take longer to come back down from it.",
      technicalDetail:
        "SLC6A4 encodes the serotonin transporter — the protein that clears serotonin from the synapse after it's used. The 'short' allele produces fewer transporters, making the system less efficient and more reactive to stressful input. With two short copies, the system down-regulates over time; the background 'everything is okay' signal runs quieter. This is one of the most replicated gene-mood associations in psychiatry.",
    },
    {
      gene: 'COMT',
      variant: 'Val158Met (Met/Met)',
      rsid: 'rs4680',
      genotype: 'Met/Met (two slow copies)',
      plainMeaning:
        "After a stressful event, adrenaline and the stress response linger in her brain longer than they do for most people.",
      technicalDetail:
        "COMT encodes the enzyme that breaks down dopamine and adrenaline in the prefrontal cortex — the region governing focus, decisions, and emotional regulation. The Met/Met variant reduces that enzyme activity roughly 3–4×, so stress hormones accumulate rather than clearing promptly. In calm conditions this is often an advantage (stronger working memory, heightened focus). Under pressure it becomes a liability: stressful events hit harder and the recovery window extends. Often called the 'worrier vs warrior' variant.",
    },
    {
      gene: 'MTHFR',
      variant: 'C677T heterozygous',
      rsid: 'rs1801133',
      genotype: 'One copy of the variant',
      plainMeaning:
        "She needs more usable folate than many people do, and her labs show she is running low. That matters because folate helps make serotonin and other mood chemicals.",
      technicalDetail:
        "MTHFR converts dietary folate into its active form (5-methyltetrahydrofolate). One copy of C677T reduces that conversion by roughly 30–40%. Active folate is a direct cofactor in producing serotonin, dopamine, and other monoamine neurotransmitters. Her borderline-low folate from her blood work fits this picture — she's avoiding outright deficiency, but possibly not maintaining the supply needed for optimal neurotransmitter synthesis.",
    },
  ],

  beats: [
    {
      type: 'narrative',
      heading: "Nothing looked wrong from the outside",
      body: "Priya is 34. She likes her work, has a stable relationship, and sees friends. From the outside, there is no obvious crisis. But inside, she feels anxious, flat, and easily overwhelmed. Her thyroid was checked and looked normal. Two antidepressants helped a little, then the side effects made her stop. After years of this, she starts to wonder whether this is simply her personality.",
    },
    {
      type: 'narrative',
      heading: "Two small lab clues were easy to miss",
      body: "When she logs her blood work, two results stand out. Her iron stores are low, and her folate is below range. Her thyroid is normal, so the results may not look urgent at first glance. But low iron and low folate can both worsen fatigue, low mood, and anxiety. For Priya, the folate result is not background noise. Her genetics explain why that small gap may have a bigger effect on her than it would on someone else.",
    },
    {
      type: 'reveal',
      heading: "Stress hit harder and stayed longer",
      body: "The pattern finally makes sense: Priya's brain is quicker to feel stress and slower to settle after it. Her SLC6A4 result points to a serotonin system that runs quieter at baseline and reacts strongly when stress arrives. Her COMT result means the adrenaline from that stress takes longer to clear. Neither variant causes depression or anxiety by itself. Together, they describe a nervous system that has always had to work harder to feel steady.",
    },
    {
      type: 'reveal',
      heading: "The missing ingredient was hiding in plain sight",
      body: "Her low folate matters because her body is less efficient at turning food folate into the form it can use. That usable folate helps make serotonin, dopamine, and other mood chemicals. Her MTHFR result explains why the low lab value may matter more for her: she may need more usable folate than average, and she has been getting barely enough. It is not the whole story, but it is a real missing ingredient.",
    },
    {
      type: 'action',
      heading: "Five practical next steps",
      items: [
        "Treat the low iron and low folate as real targets. Low iron stores can drive fatigue and low mood, and methylfolate can bypass the MTHFR conversion step. She should discuss the right dose and follow-up labs with her family doctor.",
        "Check and correct vitamin D. Low vitamin D is common, fixable, and linked with low mood, fatigue, and anxiety. A supplement plan followed by a retest in 3 months is a simple first step.",
        "Consider magnesium glycinate for 6–8 weeks. Magnesium supports the stress-clearing pathway that is under more pressure with her COMT result.",
        "Add steady aerobic movement most days. Brisk walking counts. For Priya, 20–30 minutes of moderate effort can help clear the dopamine and adrenaline that linger after stress.",
        "Revisit medication choices with her family doctor. Her past SSRI response may reflect dose, timing, side effects, or a need for a different mechanism. Her genetic picture gives that conversation more context.",
      ],
    },
  ],

  extraBeats: [
    {
      type: 'checkin' as const,
      heading: "Priya's 14-day check-in",
      goalLabel: 'Daily mood, anxiety & energy',
      caption:
        "Priya started methylfolate, vitamin D, and magnesium glycinate on day 1, then added a 25-minute walk most days from day 5. The first week was quiet. By day 9, she noticed the morning dread had lifted.",
      days: [
        {
          day: 1,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Anxiety', value: 7 },
            { label: 'Energy', value: 3 },
          ],
          note: "Started supplements today. Feeling pretty low — long week.",
        },
        {
          day: 2,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Anxiety', value: 6 },
            { label: 'Energy', value: 4 },
          ],
          note: "Slept a bit better. Still flat.",
        },
        {
          day: 3,
          scores: [
            { label: 'Mood', value: 4 },
            { label: 'Anxiety', value: 6 },
            { label: 'Energy', value: 4 },
          ],
          note: "Hard to tell if anything is changing yet.",
        },
        {
          day: 4,
          scores: [
            { label: 'Mood', value: 3 },
            { label: 'Anxiety', value: 7 },
            { label: 'Energy', value: 3 },
          ],
          note: "Stressful day. Back to square one.",
        },
        {
          day: 5,
          scores: [
            { label: 'Mood', value: 4 },
            { label: 'Anxiety', value: 5 },
            { label: 'Energy', value: 5 },
          ],
          note: "First walk — 25 minutes. Felt better for it.",
          milestone: "Started daily walks",
        },
        {
          day: 6,
          scores: [
            { label: 'Mood', value: 5 },
            { label: 'Anxiety', value: 5 },
            { label: 'Energy', value: 5 },
          ],
          note: "Another walk. The afternoon brain fog was lighter.",
        },
        {
          day: 7,
          scores: [
            { label: 'Mood', value: 5 },
            { label: 'Anxiety', value: 4 },
            { label: 'Energy', value: 6 },
          ],
          note: "First time this week I didn't feel completely drained by 3pm.",
        },
        {
          day: 8,
          scores: [
            { label: 'Mood', value: 5 },
            { label: 'Anxiety', value: 5 },
            { label: 'Energy', value: 5 },
          ],
          note: "Bit of a dip today. Didn't walk.",
        },
        {
          day: 9,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Anxiety', value: 4 },
            { label: 'Energy', value: 6 },
          ],
          note: "Something shifted. Woke up and didn't immediately feel dread.",
          milestone: "First good morning",
        },
        {
          day: 10,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Anxiety', value: 3 },
            { label: 'Energy', value: 7 },
          ],
          note: "Best day in weeks. Finished a project I'd been avoiding.",
        },
        {
          day: 11,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Anxiety', value: 3 },
            { label: 'Energy', value: 6 },
          ],
          note: "Still good. Walk felt easy today.",
        },
        {
          day: 12,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Anxiety', value: 3 },
            { label: 'Energy', value: 7 },
          ],
          note: "Slept through the night — first time in months.",
        },
        {
          day: 13,
          scores: [
            { label: 'Mood', value: 6 },
            { label: 'Anxiety', value: 4 },
            { label: 'Energy', value: 6 },
          ],
          note: "Bit anxious ahead of a presentation. But I recovered faster than usual.",
        },
        {
          day: 14,
          scores: [
            { label: 'Mood', value: 7 },
            { label: 'Anxiety', value: 3 },
            { label: 'Energy', value: 7 },
          ],
          note: "Two weeks in. I feel like I have some baseline back.",
        },
      ],
    },
  ],

  cta: {
    namedPrompt: 'serotonin-priya',
    label: 'Explore your mood & serotonin genetics in Coach',
  },
}
